import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getCookieConsent, getCookiePreferences, saveCookieConsent, subscribeCookieConsent,
  NECESSARY_ONLY, OPEN_COOKIE_NOTICE,
} from '../../utils/cookieNotice';
import './CookieNotice.css';

export default function CookieNotice() {
  const { t } = useTranslation('cookies');
  const [visible, setVisible] = useState(() => !getCookieConsent());
  const [settings, setSettings] = useState(false);
  const [preferences, setPreferences] = useState(getCookiePreferences);
  const [storageError, setStorageError] = useState(false);

  useEffect(() => {
    const open = () => {
      setPreferences(getCookiePreferences());
      setStorageError(false);
      setSettings(true);
      setVisible(true);
    };
    const unsubscribe = subscribeCookieConsent(saved => {
      setPreferences(saved);
      setVisible(!getCookieConsent());
      setSettings(false);
    });
    window.addEventListener(OPEN_COOKIE_NOTICE, open);
    return () => {
      unsubscribe();
      window.removeEventListener(OPEN_COOKIE_NOTICE, open);
    };
  }, []);

  const save = value => {
    const persisted = saveCookieConsent(value);
    if (!persisted && (value.analytics || value.marketing)) {
      setStorageError(true);
      return;
    }
    setPreferences(value);
    setSettings(false);
    setVisible(false);
    setStorageError(false);
  };
  const reject = () => save(NECESSARY_ONLY);
  const close = () => {
    if (!settings) return reject();
    setSettings(false);
    setPreferences(getCookiePreferences());
    if (getCookieConsent()) setVisible(false);
  };
  const toggle = category => setPreferences(value => ({ ...value, [category]: !value[category] }));

  return (
    <Modal show={visible} onHide={close} centered scrollable dialogClassName="cookie-dialog"
      contentClassName="cookie-panel" aria-labelledby="cookie-panel-title" aria-describedby="cookie-panel-description">
      <Modal.Body>
        <div className="cookie-panel__heading">
          <span className="cookie-panel__icon" aria-hidden="true"><i className="bi bi-shield-check" /></span>
          <div><span className="cookie-panel__eyebrow">FLWRK · {t('privacy')}</span>
            <h2 id="cookie-panel-title">{t(settings ? 'settingsTitle' : 'title')}</h2>
          </div>
        </div>
        <p id="cookie-panel-description">{t(settings ? 'settingsDescription' : 'description')}</p>

        {settings && <div className="cookie-panel__categories">
          {['necessary', 'analytics', 'marketing'].map(category => (
            <div className={'cookie-category ' + (category === 'necessary' ? 'cookie-category--necessary' : '')} key={category}>
              <div className="cookie-category__copy">
                <label htmlFor={'cookie-' + category}>{t(category)}</label>
                <p id={'cookie-' + category + '-description'}>{t(category + 'Desc')}</p>
                <p className="cookie-category__inventory">{t(category + 'Inventory')}</p>
              </div>
              <input id={'cookie-' + category} type="checkbox" role="switch"
                checked={category === 'necessary' || preferences[category]}
                disabled={category === 'necessary'}
                aria-describedby={'cookie-' + category + '-description'}
                onChange={() => toggle(category)} />
            </div>
          ))}
        </div>}

        <Link className="cookie-panel__policy" to="/privacy-policy" onClick={() => { setVisible(false); setSettings(false); }}>
          {t('policyLink')} <i className="bi bi-arrow-up-right" aria-hidden="true" />
        </Link>
        {settings && <p className="cookie-panel__retention">{t('retention')}</p>}
        {storageError && <p role="alert" className="cookie-panel__error">{t('storageError')}</p>}

        <div className="cookie-panel__actions">
          <button type="button" className="cookie-panel__button" onClick={reject}>{t('reject')}</button>
          <button type="button" className="cookie-panel__button" onClick={() => save(settings ? preferences : { necessary: true, analytics: true, marketing: true })}>
            {t(settings ? 'save' : 'acceptAll')}
          </button>
        </div>
        <button type="button" className="cookie-panel__settings" onClick={() => settings ? close() : setSettings(true)}>
          {t(settings ? 'back' : 'settings')}
        </button>
      </Modal.Body>
    </Modal>
  );
}
